import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, Copy, Link2, Mail, Plus, Send, Trash2 } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import SearchableSelect from '../../components/crm/SearchableSelect';
import { Btn, INPUT, LABEL, errMsg } from '../../components/crm/billing/ui';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TABS = [
  { key: 'reminders', label: 'Payment reminders', icon: BellRing },
  { key: 'customers', label: 'Statements & portal', icon: Link2 },
];
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const whenLabel = (n) => (n < 0 ? `${-n} day${n === -1 ? '' : 's'} before due` : n === 0 ? 'On the due date' : `${n} day${n === 1 ? '' : 's'} overdue`);
const STATUS_TONE = { sent: 'bg-[#E6F4EA] text-[#2E844A]', partial: 'bg-[#FEF8E8] text-[#7A5300]', failed: 'bg-[#FEF1EE] text-[#BA0517]', sending: 'bg-[#F3F2F2] text-[#706E6B]' };

// Sales ↔ finance automation switches.
function CreditSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/crm/customer-finance/credit-settings').then((r) => setS(r.data.settings)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async (patch) => {
    const next = { ...s, ...patch };
    try { const r = await api.put('/crm/customer-finance/credit-settings', next); setS(r.data.settings); } catch (e) { alertService.error('Not saved', errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#EEF1F6]">
      <div className="w-40"><label className={LABEL} htmlFor="cs-terms">Default payment terms (days)</label><input id="cs-terms" type="number" min="0" max="365" className={INPUT} defaultValue={s.defaultPaymentTermsDays} onBlur={(e) => Number(e.target.value) !== s.defaultPaymentTermsDays && save({ defaultPaymentTermsDays: Number(e.target.value) })} /></div>
      <div className="w-72"><label className={LABEL} htmlFor="cs-action">When an invoice goes over a customer’s credit limit</label>
        <select id="cs-action" className={INPUT} value={s.overLimitAction} onChange={(e) => save({ overLimitAction: e.target.value })}>
          <option value="warn">Warn, but allow sending</option><option value="block">Block sending (finance can override)</option>
        </select></div>
      <p className="text-[12px] text-[#706E6B] w-full">Credit limits, hold and per-customer terms are set on each account or contact, under Finance. A customer on credit hold can never be sent a new invoice.</p>
    </div>
  );
}

function AutomationCard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get('/crm/collections/automation').then((r) => setA(r.data.automation)).catch(() => {}); }, []);
  if (!a) return null;
  const save = async (patch) => {
    const next = { ...a, ...patch };
    setA(next);
    try { await api.put('/crm/collections/automation', next); } catch (e) { alertService.error('Not saved', errMsg(e)); setA(a); }
  };
  return (
    <section className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-col gap-2">
      <h2 className="text-[14px] font-semibold">Sales and finance together</h2>
      <label className="flex items-start gap-2 text-[13px]"><input type="checkbox" className="mt-0.5" checked={a.winDealOnQuoteAccept} onChange={(e) => save({ winDealOnQuoteAccept: e.target.checked })} />
        <span>Mark the deal won when its quotation is accepted<span className="block text-[12px] text-[#706E6B]">Winning a deal then offers to create the invoice.</span></span></label>
      <label className="flex items-start gap-2 text-[13px]"><input type="checkbox" className="mt-0.5" checked={a.overdueTasks} onChange={(e) => save({ overdueTasks: e.target.checked })} />
        <span>Create a follow-up task when an invoice goes overdue<span className="block text-[12px] text-[#706E6B]">Assigned to the customer’s owner, due the next day.</span></span></label>
      <CreditSettings />
      <p className="text-[12px] text-[#706E6B]">Payments, overdue invoices, credit notes, reminders and quotation decisions always appear on the customer’s and deal’s timeline, and can start workflows (Workflows → trigger “payment received”, “invoice paid”, “invoice overdue”, “quotation accepted”) and webhooks (invoice.paid, payment.received, invoice.overdue, quotation.accepted and more).</p>
    </section>
  );
}

function RemindersTab() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get('/crm/collections/dunning'), api.get('/crm/collections/reminders')]);
      setS(a.data.settings); setLog(b.data.reminders || []);
    } catch (e) { alertService.error('Could not load reminders', errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <p className="text-[13px] text-[#706E6B]">Loading…</p>;
  const setStage = (i, patch) => setS({ ...s, stages: s.stages.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const save = async (next = s) => {
    setSaving(true);
    try { const r = await api.put('/crm/collections/dunning', next); setS(r.data.settings); alertService.success('Reminder schedule saved'); } catch (e) { alertService.error('Not saved', errMsg(e)); } finally { setSaving(false); }
  };
  const runNow = async () => {
    try { const r = await api.post('/crm/collections/dunning/run'); alertService.success('Reminders sent', `${r.data.result.sent} sent, ${r.data.result.failed} failed`); load(); } catch (e) { alertService.error('Run failed', errMsg(e)); }
  };
  const usesWhatsApp = s.stages.some((x) => x.enabled && x.channel !== 'email');

  return (
    <div className="flex flex-col gap-4">
      <AutomationCard />
      <section className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-[14px] font-semibold">
            <input type="checkbox" checked={s.enabled} onChange={(e) => save({ ...s, enabled: e.target.checked })} />
            Send payment reminders automatically
          </label>
          <div className="flex gap-2">
            <Btn onClick={runNow} disabled={!s.enabled}><Send size={13} className="inline -mt-0.5 mr-1" />Send due reminders now</Btn>
            <Btn primary onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : 'Save schedule'}</Btn>
          </div>
        </div>
        <p className="text-[12px] text-[#706E6B]">
          Runs every morning. Each open invoice gets the latest step it has reached, once — switching this on for old overdue invoices sends one reminder each, not the whole series.
          EMI invoices are chased per instalment. Pause an invoice from the log below or the invoice itself if it’s disputed.
        </p>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={s.includePortalLink} onChange={(e) => setS({ ...s, includePortalLink: e.target.checked })} />
          Include the customer’s portal link (view and pay invoices online)
        </label>
        <div className="flex flex-col divide-y divide-[#EEF1F6] border border-[#D8DDE6] rounded">
          {s.stages.map((st, i) => (
            <div key={i} className="p-3 flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <input type="checkbox" aria-label="Step on" checked={st.enabled} onChange={(e) => setStage(i, { enabled: e.target.checked })} />
                <input aria-label="Days from due date" type="number" className={cn(INPUT, 'w-20')} value={st.offsetDays} onChange={(e) => setStage(i, { offsetDays: e.target.value })} />
                <span className="text-[13px] w-40">{whenLabel(Number(st.offsetDays))}</span>
                <select aria-label="Channel" className={cn(INPUT, 'w-40')} value={st.channel} onChange={(e) => setStage(i, { channel: e.target.value })}>
                  <option value="email">Email</option><option value="whatsapp">WhatsApp</option><option value="both">Email and WhatsApp</option>
                </select>
                <span className="flex-1 text-[13px] truncate text-[#706E6B]">{st.subject}</span>
                <button type="button" className="text-[12px] text-[#0176D3] hover:underline" onClick={() => setOpen(open === i ? null : i)}>{open === i ? 'Close' : 'Edit message'}</button>
                <button type="button" aria-label="Remove step" className="p-1 text-[#706E6B] hover:text-[#BA0517]" onClick={() => setS({ ...s, stages: s.stages.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
              </div>
              {open === i && (
                <div className="flex flex-col gap-2 pl-6">
                  <div><label className={LABEL} htmlFor={`st-sub-${i}`}>Email subject</label><input id={`st-sub-${i}`} className={INPUT} value={st.subject} onChange={(e) => setStage(i, { subject: e.target.value })} /></div>
                  <div><label className={LABEL} htmlFor={`st-body-${i}`}>Email message</label><textarea id={`st-body-${i}`} rows={7} className={cn(INPUT, 'h-auto py-2')} value={st.body} onChange={(e) => setStage(i, { body: e.target.value })} /></div>
                  <p className="text-[11px] text-[#706E6B]">Fill-ins: {s.placeholders.map((p) => `{{${p}}}`).join(' ')}. {'{{payLine}}'} becomes the payment link, or the portal link.</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Btn onClick={() => setS({ ...s, stages: [...s.stages, { offsetDays: (Math.max(...s.stages.map((x) => Number(x.offsetDays)), 0) + 15), enabled: true, channel: 'email', subject: 'Invoice {{invoiceNumber}} is {{daysOverdue}} days overdue', body: 'Hi {{customer}},\n\nInvoice {{invoiceNumber}} for {{amountDue}} is still unpaid.\n\n{{payLine}}\n\n{{company}}' }] })}>
            <Plus size={13} className="inline -mt-0.5 mr-1" />Add step
          </Btn>
          {usesWhatsApp && (
            <>
              <div className="w-60"><label className={LABEL} htmlFor="wa-tpl">Approved WhatsApp template</label><input id="wa-tpl" className={INPUT} value={s.whatsappTemplate.name} onChange={(e) => setS({ ...s, whatsappTemplate: { ...s.whatsappTemplate, name: e.target.value } })} /></div>
              <div className="w-24"><label className={LABEL} htmlFor="wa-lang">Language</label><input id="wa-lang" className={INPUT} value={s.whatsappTemplate.language} onChange={(e) => setS({ ...s, whatsappTemplate: { ...s.whatsappTemplate, language: e.target.value } })} /></div>
              <p className="text-[11px] text-[#706E6B] w-full">The template body needs 5 variables, in order: customer name, invoice number, amount due, due date, payment link.</p>
            </>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-[14px] font-semibold">Recent reminders</h2>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-x-auto">
          <table className="w-full text-[12px] sm:min-w-[40rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>{['Sent', 'Invoice', 'Step', 'To', 'Amount due', 'Result', ''].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {log.map((r) => (
                <tr key={r._id} className="border-t border-[#F3F2F2]">
                  <td className="px-3 py-2">{fmtDateTime(r.createdAt)}</td>
                  <td className="px-3 py-2 font-semibold">{r.invoiceNumber}</td>
                  <td className="px-3 py-2">{r.stage.startsWith('manual') ? 'Sent by hand' : whenLabel(r.offsetDays)}</td>
                  <td className="px-3 py-2">{r.recipient || '—'}</td>
                  <td className="px-3 py-2 tabular-nums">{formatCurrency(r.amountDue, r.currency)}</td>
                  <td className="px-3 py-2"><span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase', STATUS_TONE[r.status])}>{r.status}</span>{r.error && <span className="block text-[11px] text-[#706E6B]">{r.error}</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-[12px]">
                    <button type="button" className="text-[#0176D3] hover:underline mr-3" onClick={async () => { try { await api.post(`/crm/collections/invoices/${r.invoiceId}/remind`); alertService.success('Reminder sent'); load(); } catch (e) { alertService.error('Not sent', errMsg(e)); } }}>Remind again</button>
                    <button type="button" className="text-[#706E6B] hover:underline" onClick={async () => { try { await api.post(`/crm/collections/invoices/${r.invoiceId}/pause`, { paused: true }); alertService.success(`Reminders paused for ${r.invoiceNumber}`); } catch (e) { alertService.error('Not paused', errMsg(e)); } }}>Pause</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!log.length && <p className="text-[13px] text-[#706E6B] text-center py-8">No reminders sent yet.</p>}
        </div>
      </section>
    </div>
  );
}

function CustomersTab() {
  const [kind, setKind] = useState('account');
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState('');
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState('');
  const [mail, setMail] = useState({ email: '', from: '', to: '', includePortalLink: true });
  const [busy, setBusy] = useState(false);
  const ref = { [kind === 'account' ? 'accountId' : 'contactId']: partyId };

  useEffect(() => {
    setPartyId(''); setStatus(null); setLink('');
    api.get(kind === 'account' ? '/crm/accounts' : '/crm/contacts', { params: { limit: 500 } })
      .then((res) => { const list = res.data.accounts || res.data.contacts || res.data || []; setParties(list.map((p) => ({ _id: p._id, name: p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || 'Unnamed' }))); })
      .catch(() => setParties([]));
  }, [kind]);
  useEffect(() => {
    if (!partyId) return;
    setLink('');
    api.get('/crm/collections/portal', { params: ref }).then((r) => { setStatus(r.data.portal); setMail((m) => ({ ...m, email: r.data.portal.email || '' })); }).catch(() => setStatus(null));
  }, [partyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const call = async (fn, done) => {
    setBusy(true);
    try { await fn(); if (done) alertService.success(done); const r = await api.get('/crm/collections/portal', { params: ref }); setStatus(r.data.portal); } catch (e) { alertService.error('Not done', errMsg(e)); } finally { setBusy(false); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text).then(() => alertService.success('Link copied')).catch(() => {}); };

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div><label className={LABEL} htmlFor="cp-kind">Customer type</label><select id="cp-kind" className={INPUT} value={kind} onChange={(e) => setKind(e.target.value)}><option value="account">Account</option><option value="contact">Contact</option></select></div>
        <div className="sm:col-span-3"><span className={LABEL}>Customer</span><SearchableSelect value={partyId} onChange={setPartyId} options={parties} placeholder="Choose customer…" /></div>
      </div>
      {partyId && status && (
        <>
          <section className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-col gap-3">
            <h2 className="text-[14px] font-semibold flex items-center gap-2"><Link2 size={15} />Customer portal</h2>
            <p className="text-[12px] text-[#706E6B]">A private page where {status.customer} can see their invoices, download a statement and pay online. Links last 90 days; creating a new one switches off the old one.</p>
            <p className="text-[13px]">{status.active ? <>Link is active until {fmtDate(status.expiresAt)}{status.lastViewedAt ? ` · last opened ${fmtDateTime(status.lastViewedAt)}` : ' · not opened yet'}</> : 'No active link.'}</p>
            {link && (
              <div className="flex gap-2">
                <input readOnly aria-label="Portal link" className={INPUT} value={link} onFocus={(e) => e.target.select()} />
                <Btn onClick={() => copy(link)}><Copy size={13} className="inline -mt-0.5" /></Btn>
              </div>
            )}
            <div className="flex gap-2">
              <Btn primary disabled={busy} onClick={() => call(async () => { const r = await api.post('/crm/collections/portal', ref); setLink(r.data.portal.url); })}>{status.active ? 'Create new link' : 'Create link'}</Btn>
              {status.active && <Btn danger disabled={busy} onClick={() => call(() => api.post('/crm/collections/portal/revoke', ref).then(() => setLink('')), 'Link switched off')}>Switch off link</Btn>}
            </div>
          </section>
          <section className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex flex-col gap-3">
            <h2 className="text-[14px] font-semibold flex items-center gap-2"><Mail size={15} />Email statement of account</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className={LABEL} htmlFor="st-email">Send to</label><input id="st-email" type="email" className={INPUT} value={mail.email} onChange={(e) => setMail({ ...mail, email: e.target.value })} /></div>
              <div><label className={LABEL} htmlFor="st-from">From (optional)</label><input id="st-from" type="date" className={INPUT} value={mail.from} onChange={(e) => setMail({ ...mail, from: e.target.value })} /></div>
              <div><label className={LABEL} htmlFor="st-to">To (optional)</label><input id="st-to" type="date" className={INPUT} value={mail.to} onChange={(e) => setMail({ ...mail, to: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={mail.includePortalLink} onChange={(e) => setMail({ ...mail, includePortalLink: e.target.checked })} />Include a portal link (creates a new one)</label>
            <div><Btn primary disabled={busy || !mail.email} onClick={() => call(async () => {
              const r = await api.post('/crm/collections/statements/send', { ...ref, ...mail, from: mail.from || undefined, to: mail.to || undefined });
              if (r.data.portalLink) setLink(r.data.portalLink);
            }, `Statement sent to ${mail.email}`)}>Send statement</Btn></div>
            <p className="text-[11px] text-[#706E6B]">Sent from your own email account (Settings → Email), with the PDF statement attached.</p>
          </section>
        </>
      )}
    </div>
  );
}

export default function CollectionsPage() {
  const [tab, setTab] = useState('reminders');
  return (
    <div className={PAGE}>
      <PageHeader title="Collections" subtitle="Payment reminders, statements and the customer portal." icon={BellRing} />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'reminders' ? <RemindersTab /> : <CustomersTab />}
    </div>
  );
}
